import type { ReactNode, Ref } from 'react';
import { Box, Typography } from '@mui/material';
import { NavLink } from 'react-router-dom';

export interface SettingsNavItem {
  key: string;
  /** Item label (translated copy). */
  label: string;
  /** Route the item links to. */
  to: string;
  /** Leading glyph. */
  icon?: ReactNode;
  /** Optional count badge. */
  badge?: number;
}

export interface SettingsNavGroup {
  key: string;
  /** Group heading (translated copy). */
  title: string;
  items: SettingsNavItem[];
}

export interface SettingsNavProps {
  /** Grouped navigation model (e.g. the Settings catalog/budgets/... groups). */
  groups: SettingsNavGroup[];
  ref?: Ref<HTMLElement>;
}

/**
 * A grouped, route-aware section navigation rail. Each group renders an
 * uppercase title over its items; the item matching the active route is tinted
 * with the accent (secondary) tone. Generic and presentational — the config
 * (labels, routes, icons) is supplied by the caller — so it backs the Settings
 * two-pane shell here and any future grouped sub-nav.
 */
export function SettingsNav({ groups, ref }: SettingsNavProps) {
  return (
    <Box component="nav" ref={ref} sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {groups.map((group) => (
        <Box key={group.key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            component="div"
            sx={{
              px: '10px',
              pt: '4px',
              pb: '4px',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              color: 'text.secondary',
            }}
          >
            {group.title}
          </Typography>
          {group.items.map((item) => (
            <Box
              key={item.key}
              component={NavLink}
              to={item.to}
              end
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: '10px',
                py: '8px',
                borderRadius: '6px',
                textDecoration: 'none',
                color: 'text.primary',
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.3,
                '& .section-nav-icon': { color: 'text.secondary', display: 'inline-flex' },
                '&:hover': { bgcolor: 'action.hover' },
                '&.active': {
                  bgcolor: (t) =>
                    `color-mix(in srgb, ${t.palette.secondary.main} 10%, transparent)`,
                  color: 'secondary.main',
                  fontWeight: 600,
                  '& .section-nav-icon': { color: 'secondary.main' },
                },
              }}
            >
              {item.icon && (
                <Box component="span" className="section-nav-icon" sx={{ fontSize: 18 }}>
                  {item.icon}
                </Box>
              )}
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                {item.label}
              </Box>
              {item.badge != null && (
                <Box
                  component="span"
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    borderRadius: '10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}
                >
                  {item.badge}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
